<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import PropertySchedule2 from "./PropertySchedule2.vue";
import PropertyScope from "./PropertyScope.vue";
import TradesTotals from "./TradesTotals.vue";
import Schedule3 from "./Schedule3.vue";
import ContractMenu from "../../components/ContractMenu.vue";
import PDFVuer from "./PDFVuer.vue";
import { useStore } from "vuex/dist/vuex.cjs.js";
import ScrollUpButton from './ScrollUpButton.vue';
import { useToast } from "vue-toast-notification";
import axios from "axios";
import LoadingSpinner from "../../components/LoadingSpinner.vue";

// Props
const props = defineProps({
    property: {
        type: Object,
        required: true
    },
    loadContract: {
        type: Function,
    },
    selectedId: {
        type: Number,
    },
    contract: {
        type: Object
    },
});

const propertyActions = ref({
    "save": () => {
    },
    "print": () => {
    },
    "reset": () => {
    }
})
const route = useRoute();
const contractObj = ref({})
const showPdf = ref(false)
const contractId = route.params.id;
const store = useStore();
const toast = useToast();
const loading = ref(false);


watch(() => props.contract, (val) => {
    contractObj.value = val
}, { immediate: true })

const disabledEdit = computed(() => contractObj.value.status !== 1);


const setActions = (action) => {
    propertyActions.value = action
}

const currentTab = ref('scope')

// TODO implement unsaved changes indicator and logic
const unsavedChanges = ref(false)
const setUnsavedChanges = (value) => {
    unsavedChanges.value = value
}

const resetMenuOpen = ref(false)
const toggleResetMenu = () => {
    resetMenuOpen.value = !resetMenuOpen.value
}
const closeResetMenu = () => {
    resetMenuOpen.value = false
}

const viewGeneratedScope = async () => {
    loading.value = true;
    const token = sessionStorage.getItem("token");
    try {
        // Set responseType to 'arraybuffer' to handle binary data (PDF)
        const response = await axios.get(
            `${store.state.apiEndpoint}/property/download_scope/${props.property.id}/`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: "arraybuffer",
            }
        );

        // Create a Blob from the response data
        const blob = new Blob([response.data], { type: "application/pdf" });

        // Create an Object URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Open a new tab with the PDF
        const newTab = window.open("", "_blank");
        newTab.location.href = url;

        // Clean up the Object URL
        window.URL.revokeObjectURL(url);
    } catch (error) {
        toast.error("An error occurred while generating");
        console.error(error);
    } finally {
        loading.value = false;
    }

}
</script>

<template>
    <LoadingSpinner v-if="loading" message="Grabbing scope..." :fullScreen="true" />

    <div class="row card shadow-lg p-2 mb-3 sticky-top" style="top: 1rem;">
        <ScrollUpButton />

        <div class="d-flex justify-content-between align-items-center ard-body flex-wrap">
            <ul class="nav nav-pills" id="myTab" role="tablist">
                <li class="nav-item" role="presentation">
                    <button class="nav-link active" id="scope-tab" data-bs-toggle="tab" data-bs-target="#scope"
                        type="button" @click="currentTab = 'scope'" role="tab" aria-controls="scope"
                        aria-selected="false">
                        <i class="fas fa-th-list"></i>
                        <span class="ms-1">Scope</span></button>
                    <div v-if="currentTab === 'scope'" class="border border-primary mx-2 border-3"></div>
                </li>
                <li class="nav-item" role="presentation">
                    <button class="nav-link" id="trades-tab" data-bs-toggle="tab" data-bs-target="#trades"
                        type="button" @click="currentTab = 'trades'" role="tab" aria-controls="trades"
                        aria-selected="false">
                        <i class="fas fa-hard-hat"></i>
                        <span class="ms-1">Scope Trades Totals</span></button>
                    <div v-if="currentTab === 'trades'" class="border border-primary mx-2 border-3"></div>
                </li>
                <li class="nav-item" role="presentation">
                    <button class="nav-link" id="schedule2-tab" data-bs-toggle="tab" data-bs-target="#schedule2"
                        @click="currentTab = 'schedule2'" type="button" role="tab" aria-controls="schedule2"
                        aria-selected="true">
                        <i class="fas fa-table"></i>
                        <span class="ms-1">Tender Schedule 2</span>
                    </button>
                    <div v-if="currentTab === 'schedule2'" class="border border-primary mx-2 border-3"></div>
                </li>
                <li class="nav-item" role="presentation">
                    <button class="nav-link" id="schedule3-tab" data-bs-toggle="tab" data-bs-target="#schedule3"
                        @click="currentTab = 'schedule3'" type="button" role="tab" aria-controls="schedule3"
                        aria-selected="true">
                        <i class="fas fa-dollar-sign"></i>
                        <span class="ms-1">Tender Schedule 3 (EESHP)</span>
                    </button>
                    <div v-if="currentTab === 'schedule3'" class="border border-primary mx-2 border-3"></div>
                </li>
            </ul>
            <div class="d-flex gap-2">
                <div class="d-flex align-items-center badge badge-info">
                    <span class="text-muted me-2">Builders' Margin:</span>
                    <span class="fwt-bold" style="font-weight: 1000;"> {{ property?.builders_margin }}%</span>
                </div>
                <div class="d-flex align-items-center gap-2 flex-wrap justify-content-end actions-col">
                    <button @click="() => showPdf = true" target="_blank" data-bs-toggle="tooltip"
                        v-if="property.metadata['first_page']"
                        title="Click here to view the scope in the original contract" data-bs-placement="top"
                        class="btn m-0 btn-link text-info">
                        <i class="fas fa-bookmark me-2"></i>View Original Scope
                    </button>

                    <button @click="viewGeneratedScope" target="_blank" data-bs-toggle="tooltip"
                        v-if="property.metadata['first_page']"
                        title="Click here to view the scope in the original contract" data-bs-placement="top"
                        class="btn m-0 btn-link text-info">
                        <i class="fas fa-file me-2"></i>View Generated Scope
                    </button>

                    <ContractMenu :scopeFileUrl="contractObj?.scope" />


                    <div v-if="!disabledEdit">
                        <div v-if="propertyActions.resetAll || propertyActions.resetCosts" class="position-relative">
                            <button class="btn m-0 btn-link text-info d-inline-flex align-items-center reset-toggle"
                                :class="[resetMenuOpen ? 'show' : '']"
                                id="resetSubmenu" data-bs-toggle="dropdown" aria-expanded="false"
                                @click="toggleResetMenu">
                                <i class="fas fa-sync me-2"></i>Reset
                            </button>
                            <ul class="px-2 py-3 dropdown-menu dropdown-menu-end me-sm-n4"
                                :class="resetMenuOpen ? 'show' : ''" style="font-size: 9px !important; min-width: 220px;"
                                aria-labelledby="resetSubmenu">
                                <li class="mb-2">
                                    <a class="btn btn-link m-0"
                                        @click="() => { closeResetMenu(); propertyActions.resetAll(); }">
                                        <div class="d-flex align-items-center gap-3 text-dark">
                                            <div class="my-auto text-danger">
                                                <i class="fas fa-exclamation-triangle"></i>
                                            </div>
                                            <div class="text-danger">Reset all (scope)</div>
                                        </div>
                                    </a>
                                </li>
                                <li>
                                    <a class="btn btn-link m-0"
                                        @click="() => { closeResetMenu(); propertyActions.resetCosts(); }">
                                        <div class="d-flex align-items-center gap-3 text-dark">
                                            <div class="my-auto">
                                                <i class="fas fa-dollar-sign"></i>
                                            </div>
                                            <div>Reset costs only</div>
                                        </div>
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <button v-else @click="propertyActions.reset" data-bs-toggle="tooltip"
                            v-if="propertyActions.reset" title="Clicking this will reset the tables below"
                            data-bs-placement="top" class="btn m-0 btn-link text-info">
                            <i class="fas fa-sync me-2"></i>Reset
                        </button>
                    </div>

                    <button class="btn m-0 bg-gradient-danger" :class="[
                        unsavedChanges ? ' pulse-button' : ''
                    ]" v-if="!disabledEdit" @click="propertyActions.save">
                        <i class="fas fa-save me-2"></i>
                        Save
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div class="row card shadow-lg scheduled-2 pt-2">
        <div class="card-body p-3">
            <div class="tab-content p-3" id="myTabContent">
                <div class="tab-pane fade" id="schedule2" role="tabpanel" aria-labelledby="schedule2-tab">
                    <PropertySchedule2 :property="property" v-if="currentTab === 'schedule2'"
                        :contractStatus="contractObj?.status" :scopeFileUrl="contractObj?.scope"
                        :setActions="setActions" :loadContract="loadContract" />
                </div>
                <div class="tab-pane fade" id="trades" role="tabpanel" aria-labelledby="trades-tab">
                    <TradesTotals v-if="currentTab === 'trades'" :propertyId="property.id"
                        :propertyMetadata="property.metadata" :setActions="setActions" />
                </div>
                <div class="tab-pane fade show active" id="scope" role="tabpanel" aria-labelledby="scope-tab">
                    <PropertyScope v-if="currentTab === 'scope'" :propertyId="property.id"
                        :contractId="contractObj?.contract_number_id" :contractStatus="contractObj?.status"
                        :setActions="setActions" :address="property.address" :loadContract="loadContract"
                        :propertyMetadata="property.metadata" :scopeFileUrl="contractObj?.scope" />
                </div>
                <div class="tab-pane fade" id="schedule3" role="tabpanel" aria-labelledby="schedule3-tab">
                    <Schedule3 :contractSchedule3="contractObj.schedule3" :loadContract="loadContract"
                        :setActions="setActions" :tab="currentTab" :contractStatus="contractObj.status" />
                </div>
            </div>
        </div>
    </div>
    <div v-if="showPdf" class="pdf-viewer pdf-fixed-bottom">
        <PDFVuer :pdfUrl="`${store.state.mediaUrl}/contracts/${contractId}/${property.id}_scope.pdf`"
            :hidePdf="() => showPdf = false" />
    </div>
</template>

<style scoped>
.pulse-button {
    animation: pulse 2s infinite;
    position: relative;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(81, 7, 255, 0.4);
    }

    70% {
        box-shadow: 0 0 0 10px rgba(255, 193, 7, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    }
}

.btn.disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.actions-col {
    min-width: 470px;
}
.reset-toggle {
    white-space: nowrap;
}
</style>
